import type { AppState } from './state';
import type { Session } from '../types';

const ORDER: Record<Session['state'], number> = {
  blocked: 0,
  awaiting_human: 1,
  awaiting_clarification: 1,
  running: 2,
  planning: 2,
  interpreting: 2,
  suspended: 3,
  completed: 4,
  failed: 5,
  cancelled: 6,
};

export function waitingSessions(s: AppState): Session[] {
  return s.sessions
    .filter(
      (x) =>
        // una Conversazione aspetta sempre il prossimo messaggio: non è «in attesa di te»
        x.mode !== 'chat' &&
        (x.state === 'awaiting_human' ||
          x.state === 'awaiting_clarification' ||
          x.state === 'blocked'),
    )
    .sort((a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime());
}

export function openSessions(s: AppState): Session[] {
  return s.sessions.filter((x) => !['completed', 'cancelled', 'failed'].includes(x.state));
}

export function closedSessions(s: AppState): Session[] {
  return s.sessions.filter((x) => ['completed', 'cancelled', 'failed'].includes(x.state));
}

export function runningSessions(s: AppState): Session[] {
  return s.sessions.filter((x) => ['running', 'planning', 'interpreting'].includes(x.state));
}

export function sortedSessions(s: AppState): Session[] {
  const f = s.filters;
  let list = [...s.sessions].sort((a, b) => {
    const d = ORDER[a.state] - ORDER[b.state];
    if (d !== 0) return d;
    if (ORDER[a.state] === 1)
      return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });
  if (f.onlyWaiting) {
    const w = new Set(waitingSessions(s).map((x) => x.id));
    list = list.filter((x) => w.has(x.id));
  }
  if (f.state) list = list.filter((x) => x.state === f.state);
  if (f.project) list = list.filter((x) => x.project === f.project);
  if (f.search) {
    const q = f.search.toLowerCase();
    list = list.filter((x) => (x.title + ' ' + x.requestText).toLowerCase().includes(q));
  }
  return list;
}

// i progetti delle sessioni, id → nome, per il filtro delle conversazioni
export function projects(s: AppState): { id: string; name: string }[] {
  const seen = new Map<string, string>();
  for (const x of s.sessions) if (x.project) seen.set(x.project, x.projectName ?? x.project);
  return [...seen].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
}

export function sessionMessages(s: AppState, id: string) {
  return s.messages.filter((m) => m.sessionId === id);
}

export function sessionBoard(s: AppState, id: string) {
  return s.blackboard.filter((b) => b.sessionId === id);
}

export function sessionClaims(s: AppState, id: string) {
  return s.claims.filter((c) => c.sessionId === id);
}

export function hasOpenCollision(s: AppState, id: string): boolean {
  return sessionClaims(s, id).some((c) => c.state === 'collided');
}

// doppia attribuzione (§9.4.1): un solo flusso, due indici
export function traceByInstance(s: AppState, instanceId: string) {
  return s.trace.filter((t) => t.instanceId === instanceId).sort((a, b) => a.seq - b.seq);
}

export function traceBySession(s: AppState, sessionId: string) {
  return s.trace.filter((t) => t.sessionId === sessionId).sort((a, b) => a.seq - b.seq);
}

export function nodeById(s: AppState, id: string | undefined) {
  return id ? s.runtime.nodes.find((n) => n.id === id) : undefined;
}

export function laneBySession(s: AppState, sessionId: string) {
  return s.runtime.lanes.find((l) => l.sessionId === sessionId);
}

export function activeInstances(s: AppState): number {
  return s.runtime.nodes.filter(
    (n) => n.kind === 'agent' && ['working', 'engaged', 'awaiting_human'].includes(n.state),
  ).length;
}

export function dormantInstances(s: AppState): number {
  return s.runtime.nodes.filter(
    (n) => n.kind === 'agent' && ['dormant', 'released', 'draining'].includes(n.state),
  ).length;
}

// i token di oggi sono la somma della traccia: è l'unico consumo misurato davvero
export function tokensToday(s: AppState, now: Date = new Date()): number {
  const day = now.toDateString();
  return s.trace.reduce(
    (sum, t) => (t.tokens && new Date(t.at).toDateString() === day ? sum + t.tokens : sum),
    0,
  );
}

// chi sta facendo cosa: l'ultima riga di traccia di ogni agente nelle sessioni vive
export interface AgentAtWork {
  sessionId: string;
  instanceId: string;
  last: AppState['trace'][number];
  /** l'ultima cosa detta dal pop (un commento mentre lavora, una decisione, un passaggio di
   * consegne): leggibile, mai un comando, un commit o un conteggio di token */
  said: AppState['trace'][number] | null;
  steps: number;
}

const SAID = new Set(['thought', 'decision', 'handoff']);

export function agentsAtWork(s: AppState): AgentAtWork[] {
  const live = new Set(openSessions(s).map((x) => x.id));
  const byAgent = new Map<string, AgentAtWork>();
  for (const t of s.trace) {
    if (!live.has(t.sessionId)) continue;
    const key = `${t.sessionId}|${t.instanceId}`;
    const seen = byAgent.get(key);
    const said = SAID.has(t.kind) && !t.streaming && t.text.trim() ? t : null;
    if (!seen)
      byAgent.set(key, {
        sessionId: t.sessionId,
        instanceId: t.instanceId,
        last: t,
        said,
        steps: 1,
      });
    else {
      seen.steps += 1;
      if (t.seq > seen.last.seq) seen.last = t;
      if (said && (!seen.said || t.seq > seen.said.seq)) seen.said = said;
    }
  }
  return [...byAgent.values()].sort(
    (a, b) => new Date(b.last.at).getTime() - new Date(a.last.at).getTime(),
  );
}

export function sessionsToday(s: AppState, now: Date = new Date()): number {
  const day = now.toDateString();
  return s.sessions.filter((x) => new Date(x.createdAt).toDateString() === day).length;
}
